import React, { useContext, useState, useEffect } from "react";
import ThemeContext from "../../lib/theme-context";
import { portfolioService } from "../../services/api/portfolioService";
import { accountService } from "../../services/api/accountService";
import { getCurrencySymbol } from "../../services/currencyService";
import throwMessage from "../../utils/throwMessage";
import useWindowDimensions from "../../utils/width";
import CustomSingleTextField from "../../components/textfields/singleRow";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";

const Income = () => {
  const theme = useContext(ThemeContext);
  const { width } = useWindowDimensions();
  const isDark = theme[0] === "dark";

  // State
  const [accounts, setAccounts] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [outsideIncomes, setOutsideIncomes] = useState([]);
  const [stats, setStats] = useState(null);
  const [realBalances, setRealBalances] = useState([]);
  const [activeTab, setActiveTab] = useState(0);

  // Expense Groups State
  const [expenseGroups, setExpenseGroups] = useState([
    { id: "default", name: "Expenses", expenses: [] },
  ]);
  const [activeExpenseGroup, setActiveExpenseGroup] = useState("default");
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editingGroupName, setEditingGroupName] = useState("");

  // Account Dialog State
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [accountMode, setAccountMode] = useState("create");
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountForm, setAccountForm] = useState({
    name: "",
    type: "Personal",
    currency: "USD",
    accountSize: "",
    style: "Intraday",
    maxPercentPerPayout: "",
    profitSplit: "100",
    dayTradingPercent: "100",
    swingTradingPercent: "0",
  });

  // Expense Dialog State
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [expenseMode, setExpenseMode] = useState("create");
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [expenseForm, setExpenseForm] = useState({
    name: "",
    amount: "",
    currency: "USD",
    groupId: "default",
  });

  // Outside Income Dialog State
  const [outsideIncomeDialogOpen, setOutsideIncomeDialogOpen] = useState(false);
  const [outsideIncomeMode, setOutsideIncomeMode] = useState("create");
  const [selectedOutsideIncome, setSelectedOutsideIncome] = useState(null);
  const [outsideIncomeForm, setOutsideIncomeForm] = useState({
    name: "",
    amount: "",
    currency: "USD",
  });

  // Delete Confirmation State
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState("");
  const [deleteItem, setDeleteItem] = useState(null);

  // Single currency lock - once first expense/account is created, all others use that currency
  const appBaseCurrency = React.useMemo(() => {
    if (expenses?.length > 0 && expenses[0].currency)
      return expenses[0].currency;
    if (accounts?.length > 0 && accounts[0].currency)
      return accounts[0].currency;
    return "AUD";
  }, [expenses, accounts]);

  const loadData = async (groupId = activeExpenseGroup) => {
    try {
      const [
        accountsData,
        expensesData,
        outsideIncomesData,
        statsData,
        realBalancesData,
        savedGroups,
      ] = await Promise.all([
        portfolioService.getAllAccounts(),
        portfolioService.getAllExpenses(),
        portfolioService.getAllOutsideIncome(),
        portfolioService.getStats(groupId),
        portfolioService.getRealBalances(),
        accountService.getExpenseGroups(),
      ]);
      setAccounts(accountsData || []);
      setExpenses(expensesData || []);
      setOutsideIncomes(outsideIncomesData || []);

      // Organize expenses into groups
      const groupedExpenses = {};
      (expensesData || []).forEach((expense) => {
        // Legacy expenses without groupId go to "default"
        const expenseGroupId = expense.groupId || "default";
        if (!groupedExpenses[expenseGroupId]) {
          groupedExpenses[expenseGroupId] = [];
        }
        groupedExpenses[expenseGroupId].push(expense);
      });

      // Merge saved group names with expense data
      const groups = savedGroups.map((savedGroup) => ({
        ...savedGroup,
        expenses: groupedExpenses[savedGroup.id] || [],
      }));

      // Ensure at least one default group exists
      if (groups.length === 0) {
        groups.push({ id: "default", name: "Expenses", expenses: [] });
      }

      setExpenseGroups(groups);
      setStats(statsData || null);
      setRealBalances(realBalancesData || []);
    } catch (err) {
      console.error("Error loading data:", err);
      throwMessage("error", "Failed to load portfolio data");
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Load once on mount

  // ============ ACCOUNT HANDLERS ============

  const handleOpenAccountCreate = () => {
    setAccountMode("create");
    setAccountForm({
      name: "",
      type: "Personal",
      currency: appBaseCurrency,
      accountSize: "",
      style: "Intraday",
      maxPercentPerPayout: "",
      profitSplit: "100",
      dayTradingPercent: "100",
      swingTradingPercent: "0",
    });
    setSelectedAccount(null);
    setAccountDialogOpen(true);
  };

  const handleOpenAccountEdit = (account) => {
    const validStyles = ["Intraday", "Swing", "Both"];
    const accountStyle = validStyles.includes(account.style)
      ? account.style
      : "Both";
    setAccountMode("edit");
    setAccountForm({
      name: account.name,
      type: account.type,
      currency: account.currency || "USD",
      accountSize: account.accountSize.toString(),
      style: accountStyle,
      maxPercentPerPayout: account.maxPercentPerPayout.toString(),
      profitSplit: account.profitSplit.toString(),
      dayTradingPercent: (account.dayTradingPercent || 0).toString(),
      swingTradingPercent: (account.swingTradingPercent || 0).toString(),
    });
    setSelectedAccount(account);
    setAccountDialogOpen(true);
  };

  const handleAccountSubmit = async () => {
    if (
      !accountForm.name ||
      !accountForm.accountSize ||
      !accountForm.maxPercentPerPayout
    ) {
      throwMessage("warning", "Please fill in all required fields");
      return;
    }

    const dayPercent = parseFloat(accountForm.dayTradingPercent) || 0;
    const swingPercent = parseFloat(accountForm.swingTradingPercent) || 0;

    if (dayPercent + swingPercent > 100) {
      throwMessage(
        "warning",
        "Day Trading % + Swing Trading % cannot exceed 100%",
      );
      return;
    }

    try {
      const accountData = {
        name: accountForm.name,
        type: accountForm.type,
        currency: accountForm.currency,
        accountSize: parseFloat(accountForm.accountSize),
        style: accountForm.style,
        maxPercentPerPayout: parseFloat(accountForm.maxPercentPerPayout),
        profitSplit: parseFloat(accountForm.profitSplit),
        dayTradingPercent: dayPercent,
        swingTradingPercent: swingPercent,
      };

      if (accountMode === "create") {
        await portfolioService.createAccount(accountData);
        throwMessage("success", "Account created successfully");
      } else {
        await portfolioService.updateAccount({
          _id: selectedAccount._id,
          ...accountData,
        });
        throwMessage("success", "Account updated successfully");
      }

      setAccountDialogOpen(false);
      loadData();
    } catch (err) {
      console.error("Error saving account:", err);
      throwMessage("error", "Failed to save account");
    }
  };

  const handleDeleteAccount = (account) => {
    setDeleteType("account");
    setDeleteItem(account);
    setDeleteDialogOpen(true);
  };

  // ============ EXPENSE GROUP HANDLERS ============

  const handleCreateExpenseGroup = async () => {
    try {
      // Check if maximum number of groups reached
      if (expenseGroups.length >= 5) {
        throwMessage("warning", "Maximum of 5 expense groups allowed");
        return;
      }

      // Auto-generate name as "Expenses (N)"
      const nextNumber = expenseGroups.length + 1;
      const autoName = `Expenses (${nextNumber})`;
      const groupId = `expenses-${nextNumber}`;

      const newGroup = {
        id: groupId,
        name: autoName,
        expenses: [],
      };

      const updatedGroups = [...expenseGroups, newGroup];

      // Save to database (without expenses array)
      await accountService.updateExpenseGroups(
        updatedGroups.map(({ id, name }) => ({ id, name })),
      );

      setExpenseGroups(updatedGroups);
      setActiveExpenseGroup(groupId);

      // Switch to the new group's tab (Portfolio is 0, Outside Income is 1, so new group is at index 1 + expenseGroups.length)
      setActiveTab(1 + updatedGroups.length);

      throwMessage("success", `Expense group "${autoName}" created`);
    } catch (err) {
      console.error("Error creating expense group:", err);
    }
  };

  const handleRenameExpenseGroup = async (groupId, newName) => {
    if (!newName.trim()) {
      throwMessage("warning", "Group name cannot be empty");
      return;
    }

    try {
      const updatedGroups = expenseGroups.map((g) =>
        g.id === groupId ? { ...g, name: newName.trim() } : g,
      );

      // Save to database (without expenses array)
      await accountService.updateExpenseGroups(
        updatedGroups.map(({ id, name }) => ({ id, name })),
      );

      setExpenseGroups(updatedGroups);
      setEditingGroupId(null);
      setEditingGroupName("");
      throwMessage("success", "Group renamed");
    } catch (err) {
      console.error("Error renaming expense group:", err);
    }
  };

  const handleDeleteExpenseGroup = async (groupId) => {
    if (groupId === "default") {
      throwMessage("warning", "Cannot delete the default group");
      return;
    }

    const group = expenseGroups.find((g) => g.id === groupId);
    if (group && group.expenses.length > 0) {
      throwMessage(
        "warning",
        "Cannot delete a group with expenses. Delete all expenses first.",
      );
      return;
    }

    try {
      const updatedGroups = expenseGroups.filter((g) => g.id !== groupId);

      // Save to database (without expenses array)
      await accountService.updateExpenseGroups(
        updatedGroups.map(({ id, name }) => ({ id, name })),
      );

      setExpenseGroups(updatedGroups);

      if (activeExpenseGroup === groupId) {
        setActiveExpenseGroup("default");
        setActiveTab(2); // Switch to Default tab (Portfolio is 0, Outside Income is 1, Default is 2)
      }
      throwMessage("success", "Expense group deleted");
    } catch (err) {
      console.error("Error deleting expense group:", err);
    }
  };

  // ============ EXPENSE HANDLERS ============

  const handleOpenExpenseCreate = () => {
    setExpenseMode("create");
    setExpenseForm({
      name: "",
      amount: "",
      currency: appBaseCurrency,
      groupId: activeExpenseGroup,
    });
    setSelectedExpense(null);
    setExpenseDialogOpen(true);
  };

  const handleOpenExpenseEdit = (expense) => {
    setExpenseMode("edit");
    setExpenseForm({
      name: expense.name,
      amount: expense.amount.toString(),
      currency: expense.currency || "USD",
      groupId: expense.groupId || "default",
    });
    setSelectedExpense(expense);
    setExpenseDialogOpen(true);
  };

  const handleExpenseSubmit = async () => {
    if (!expenseForm.name || !expenseForm.amount) {
      throwMessage("warning", "Please fill in all fields");
      return;
    }

    try {
      const expenseData = {
        name: expenseForm.name,
        amount: parseFloat(expenseForm.amount),
        currency: expenseForm.currency,
        groupId: expenseForm.groupId,
      };

      if (expenseMode === "create") {
        await portfolioService.createExpense(expenseData);
        throwMessage("success", "Expense created successfully");
      } else {
        await portfolioService.updateExpense({
          _id: selectedExpense._id,
          ...expenseData,
        });
        throwMessage("success", "Expense updated successfully");
      }

      setExpenseDialogOpen(false);
      loadData();
    } catch (err) {
      console.error("Error saving expense:", err);
      throwMessage("error", "Failed to save expense");
    }
  };

  const handleDeleteExpense = (expense) => {
    setDeleteType("expense");
    setDeleteItem(expense);
    setDeleteDialogOpen(true);
  };

  // ============ OUTSIDE INCOME HANDLERS ============

  const handleOpenOutsideIncomeCreate = () => {
    setOutsideIncomeMode("create");
    setOutsideIncomeForm({
      name: "",
      amount: "",
      currency: appBaseCurrency,
    });
    setSelectedOutsideIncome(null);
    setOutsideIncomeDialogOpen(true);
  };

  const handleOpenOutsideIncomeEdit = (income) => {
    setOutsideIncomeMode("edit");
    setOutsideIncomeForm({
      name: income.name,
      amount: income.amount.toString(),
      currency: income.currency || "USD",
    });
    setSelectedOutsideIncome(income);
    setOutsideIncomeDialogOpen(true);
  };

  const handleOutsideIncomeSubmit = async () => {
    if (!outsideIncomeForm.name || !outsideIncomeForm.amount) {
      throwMessage("warning", "Please fill in all fields");
      return;
    }

    try {
      const incomeData = {
        name: outsideIncomeForm.name,
        amount: parseFloat(outsideIncomeForm.amount),
        currency: outsideIncomeForm.currency,
      };

      if (outsideIncomeMode === "create") {
        await portfolioService.createOutsideIncome(incomeData);
        throwMessage("success", "Outside income created successfully");
      } else {
        await portfolioService.updateOutsideIncome({
          _id: selectedOutsideIncome._id,
          ...incomeData,
        });
        throwMessage("success", "Outside income updated successfully");
      }

      setOutsideIncomeDialogOpen(false);
      loadData();
    } catch (err) {
      console.error("Error saving outside income:", err);
      throwMessage("error", "Failed to save outside income");
    }
  };

  const handleDeleteOutsideIncome = (income) => {
    setDeleteType("outsideIncome");
    setDeleteItem(income);
    setDeleteDialogOpen(true);
  };

  // ============ DELETE HANDLER ============

  const confirmDelete = async () => {
    try {
      if (deleteType === "account") {
        await portfolioService.deleteAccount(deleteItem._id);
        throwMessage("success", "Account deleted successfully");
      } else if (deleteType === "expense") {
        await portfolioService.deleteExpense(deleteItem._id);
        throwMessage("success", "Expense deleted successfully");
      } else if (deleteType === "outsideIncome") {
        await portfolioService.deleteOutsideIncome(deleteItem._id);
        throwMessage("success", "Outside income deleted successfully");
      }
      setDeleteDialogOpen(false);
      loadData();
    } catch (err) {
      console.error("Error deleting:", err);
      throwMessage("error", `Failed to delete ${deleteType}`);
    }
  };

  // ============ CALCULATIONS ============

  // Get expenses for the active group
  const activeGroupExpenses = React.useMemo(() => {
    const group = expenseGroups.find((g) => g.id === activeExpenseGroup);
    return group ? group.expenses : [];
  }, [expenseGroups, activeExpenseGroup]);

  // Recalculate stats when active expense group changes
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [statsData, realBalancesData] = await Promise.all([
          portfolioService.getStats(activeExpenseGroup),
          portfolioService.getRealBalances(),
        ]);
        setStats(statsData || null);
        setRealBalances(realBalancesData || []);
      } catch (err) {
        console.error("Error loading stats:", err);
      }
    };

    if (expenseGroups.length > 0) {
      fetchStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeExpenseGroup]);

  const calculateMonthlyNetProfit = (account) => {
    const maxPayout = (account.accountSize * account.maxPercentPerPayout) / 100;
    const afterSplit = (maxPayout * account.profitSplit) / 100;
    return afterSplit;
  };

  // Calculate real stats from actual account balances
  const calculateRealStats = () => {
    if (
      !realBalances ||
      !realBalances.accounts ||
      realBalances.accounts.length === 0
    ) {
      return {
        totalAUM: 0,
        totalCurrentBalance: 0,
        totalNetProfitLoss: 0,
        dayTradingProfit: 0,
        dayTradingPercentage: 0,
        swingTradingProfit: 0,
        swingTradingPercentage: 0,
      };
    }

    const totals = realBalances.totals || {};

    // Use the backend-calculated totalAUM which already has profit splits applied
    return {
      totalAUM: realBalances.totalAUM || 0,
      totalCurrentBalance: realBalances.totalCurrentBalance || 0,
      totalNetProfitLoss: realBalances.totalNetProfitLoss || 0,
      dayTradingProfit: totals.dayTradingProfit || 0,
      dayTradingPercentage: totals.dayTradingPercentage || 0,
      swingTradingProfit: totals.swingTradingProfit || 0,
      swingTradingPercentage: totals.swingTradingPercentage || 0,
    };
  };

  const realStats = calculateRealStats();

  // ============ STYLES ============

  const containerStyle = {
    width: "100%",
    minHeight: "100vh",
    backgroundColor: isDark ? "rgba(40, 42, 46)" : "#FCFCFF",
    padding: width < 768 ? "16px" : "24px",
    paddingBottom: width < 768 ? "80px" : "24px",
    color: isDark ? "#FCFCFF" : "#1a1b1e",
  };

  const cardStyle = {
    backgroundColor: isDark ? "#25262b" : "#ffffff",
    color: isDark ? "#FCFCFF" : "#1a1b1e",
    marginBottom: "24px",
    borderRadius: "12px",
    boxShadow: isDark
      ? "0 4px 6px rgba(0, 0, 0, 0.3)"
      : "0 4px 6px rgba(0, 0, 0, 0.1)",
  };

  const tableContainerStyle = {
    backgroundColor: isDark ? "#1a1b1e" : "#ffffff",
    borderRadius: "8px",
    overflowX: "auto",
  };

  const tableCellStyle = {
    color: isDark ? "#FCFCFF" : "#1a1b1e",
    borderBottom: isDark ? "1px solid #373A40" : "1px solid #e0e0e0",
  };

  const dialogStyle = {
    "& .MuiDialog-paper": {
      backgroundColor: isDark ? "#25262b" : "#ffffff",
      color: isDark ? "#FCFCFF" : "#1a1b1e",
      minWidth: width < 768 ? "90%" : "500px",
      zIndex: 1502,
      padding: "24px",
    },
    "& .MuiBackdrop-root": {
      zIndex: 1501,
    },
    zIndex: 1500,
  };

  return (
    <Box style={containerStyle}>
      <Box style={{ maxWidth: "1400px", margin: "0 auto" }}>
        {/* ============ SECTION 1 & 2: PORTFOLIO DASHBOARDS ============ */}
        <Box
          style={{
            display: "grid",
            gridTemplateColumns: width < 1200 ? "1fr" : "1fr 1fr",
            gap: "24px",
            marginBottom: "24px",
          }}
        >
          {/* PROJECTED DASHBOARD */}
          <Card style={cardStyle}>
            <CardContent>
              <Box
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "16px",
                  flexWrap: "wrap",
                  gap: "8px",
                }}
              >
                <Typography variant="h6" style={{ fontWeight: 600 }}>
                  Portfolio (Projected)
                </Typography>
                {stats?.exchangeRateFrom &&
                  stats?.exchangeRateFrom !== stats?.exchangeRateTo && (
                    <Typography
                      variant="caption"
                      style={{
                        fontStyle: "italic",
                        color: isDark ? "#9ca3af" : "#6b7280",
                      }}
                    >
                      {stats?.exchangeRate
                        ? `1 ${stats.exchangeRateFrom} = ${stats.exchangeRate.toFixed(4)} ${stats.exchangeRateTo}`
                        : `Exchange rate unavailable (1 ${stats.exchangeRateFrom} = 1 ${stats.exchangeRateTo} fallback)`}
                    </Typography>
                  )}
              </Box>

              <Box
                style={{
                  padding: "20px",
                  backgroundColor: isDark ? "#1a1b1e" : "#f5f5f5",
                  borderRadius: "8px",
                  border: isDark ? "1px solid #373A40" : "1px solid #e0e0e0",
                }}
              >
                <Tooltip
                  title={
                    stats?.aumByCurrency &&
                    Object.keys(stats.aumByCurrency).length > 0
                      ? `${Object.entries(stats.aumByCurrency)
                          .filter(([currency, amount]) => amount > 0)
                          .map(
                            ([currency, amount]) =>
                              `${getCurrencySymbol(currency)}${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`,
                          )
                          .join(
                            " + ",
                          )} → ${getCurrencySymbol(stats?.baseCurrency || "AUD")}${stats?.baseCurrency || "AUD"}`
                      : ""
                  }
                  arrow
                  placement="right"
                >
                  <Typography
                    variant="body2"
                    style={{
                      marginBottom: "12px",
                      fontSize: "15px",
                      cursor: "pointer",
                      display: "inline-block",
                    }}
                  >
                    <strong>Total AUM after profit splits:</strong>{" "}
                    {getCurrencySymbol(stats?.baseCurrency || "AUD")}
                    {(stats?.totalAUM || 0).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                </Tooltip>

                <Typography
                  variant="body2"
                  style={{
                    marginBottom: "12px",
                    fontWeight: 600,
                    fontSize: "15px",
                  }}
                >
                  <strong>Total Monthly Net Profit:</strong>{" "}
                  <span
                    style={{
                      color:
                        (stats?.totalMonthlyNetProfit || 0) === 0
                          ? "#ED9E0E"
                          : (stats?.totalMonthlyNetProfit || 0) > 0
                            ? "#26a65d"
                            : "#f23645",
                    }}
                  >
                    {getCurrencySymbol(stats?.baseCurrency || "AUD")}
                    {(stats?.totalMonthlyNetProfit || 0).toLocaleString(
                      undefined,
                      {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      },
                    )}
                  </span>
                </Typography>

                <Typography
                  variant="body2"
                  style={{
                    marginBottom: "12px",
                    fontSize: "15px",
                  }}
                >
                  <strong>Intraday Results:</strong>{" "}
                  <span
                    style={{
                      color:
                        (stats?.dayTradingProfit || 0) === 0
                          ? "#ED9E0E"
                          : (stats?.dayTradingProfit || 0) > 0
                            ? "#26a65d"
                            : "#f23645",
                    }}
                  >
                    {getCurrencySymbol(stats?.baseCurrency || "AUD")}
                    {(stats?.dayTradingProfit || 0).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                  , or{" "}
                  <span
                    style={{
                      color:
                        (stats?.dayTradingPercentage || 0) === 0
                          ? "#ED9E0E"
                          : (stats?.dayTradingPercentage || 0) > 0
                            ? "#26a65d"
                            : "#f23645",
                    }}
                  >
                    {(stats?.dayTradingPercentage || 0).toFixed(2)}%
                  </span>{" "}
                  total profit.
                </Typography>

                <Typography
                  variant="body2"
                  style={{
                    marginBottom: "12px",
                    fontSize: "15px",
                  }}
                >
                  <strong>Swing Results:</strong>{" "}
                  <span
                    style={{
                      color:
                        (stats?.swingTradingProfit || 0) === 0
                          ? "#ED9E0E"
                          : (stats?.swingTradingProfit || 0) > 0
                            ? "#26a65d"
                            : "#f23645",
                    }}
                  >
                    {getCurrencySymbol(stats?.baseCurrency || "AUD")}
                    {(stats?.swingTradingProfit || 0).toLocaleString(
                      undefined,
                      {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      },
                    )}
                  </span>
                  , or{" "}
                  <span
                    style={{
                      color:
                        (stats?.swingTradingPercentage || 0) === 0
                          ? "#ED9E0E"
                          : (stats?.swingTradingPercentage || 0) > 0
                            ? "#26a65d"
                            : "#f23645",
                    }}
                  >
                    {(stats?.swingTradingPercentage || 0).toFixed(2)}%
                  </span>{" "}
                  total profit.
                </Typography>

                <Divider
                  style={{
                    margin: "16px 0",
                    backgroundColor: isDark ? "#373A40" : "#e0e0e0",
                  }}
                />

                <Divider
                  style={{
                    margin: "16px 0",
                    backgroundColor: isDark ? "#373A40" : "#e0e0e0",
                  }}
                />

                <Typography
                  variant="body2"
                  style={{ marginBottom: "12px", fontSize: "15px" }}
                >
                  <strong>Outside Income:</strong>{" "}
                  <span style={{ color: "#2196f3" }}>
                    {getCurrencySymbol(stats?.baseCurrency || "AUD")}
                    {(stats?.totalOutsideIncome || 0).toLocaleString(
                      undefined,
                      {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      },
                    )}
                  </span>
                </Typography>

                <Typography
                  variant="body2"
                  style={{
                    marginBottom: "12px",
                    fontWeight: 600,
                    fontSize: "15px",
                  }}
                >
                  <strong>Total Combined Income:</strong>{" "}
                  <span
                    style={{
                      color:
                        (stats?.totalCombinedIncome || 0) === 0
                          ? "#ED9E0E"
                          : (stats?.totalCombinedIncome || 0) > 0
                            ? "#26a65d"
                            : "#f23645",
                    }}
                  >
                    {getCurrencySymbol(stats?.baseCurrency || "AUD")}
                    {(stats?.totalCombinedIncome || 0).toLocaleString(
                      undefined,
                      {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      },
                    )}
                  </span>
                </Typography>

                <Divider
                  style={{
                    margin: "16px 0",
                    backgroundColor: isDark ? "#373A40" : "#e0e0e0",
                  }}
                />

                <Typography
                  variant="body2"
                  style={{
                    marginBottom: "8px",
                    fontStyle: "italic",
                    fontSize: "15px",
                  }}
                >
                  Based on monthly expenses of{" "}
                  <span
                    style={{
                      color:
                        (stats?.totalExpenses || 0) === 0
                          ? "#ED9E0E"
                          : "#f23645",
                    }}
                  >
                    {getCurrencySymbol(stats?.baseCurrency || "AUD")}
                    {(stats?.totalExpenses || 0).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                  :
                </Typography>

                <Typography
                  variant="body2"
                  style={{ marginBottom: "8px", fontSize: "15px" }}
                >
                  My expenses are{" "}
                  <span
                    style={{
                      color:
                        (stats?.expensePercentage || 0) === 100
                          ? "#ED9E0E"
                          : (stats?.expensePercentage || 0) < 100
                            ? "#26a65d"
                            : "#f23645",
                    }}
                  >
                    {(stats?.expensePercentage || 0).toFixed(2)}%
                  </span>{" "}
                  of trading profit, or{" "}
                  <span
                    style={{
                      color:
                        (stats?.expensePercentageCombined || 0) === 100
                          ? "#ED9E0E"
                          : (stats?.expensePercentageCombined || 0) < 100
                            ? "#26a65d"
                            : "#f23645",
                    }}
                  >
                    {(stats?.expensePercentageCombined || 0).toFixed(2)}%
                  </span>{" "}
                  of combined income.
                </Typography>

                <Typography variant="body2" style={{ fontSize: "15px" }}>
                  My monthly returns cover{" "}
                  <span
                    style={{
                      color:
                        (stats?.monthsCovered || 0) === 1
                          ? "#ED9E0E"
                          : (stats?.monthsCovered || 0) >= 1
                            ? "#26a65d"
                            : "#f23645",
                    }}
                  >
                    {Math.abs(stats?.monthsCovered || 0) >= 1
                      ? `${(stats?.monthsCovered || 0).toFixed(2)} months`
                      : `${((stats?.monthsCovered || 0) * 30).toFixed(2)} days`}
                  </span>{" "}
                  (trading only), or{" "}
                  <span
                    style={{
                      color:
                        (stats?.monthsCoveredCombined || 0) === 1
                          ? "#ED9E0E"
                          : (stats?.monthsCoveredCombined || 0) >= 1
                            ? "#26a65d"
                            : "#f23645",
                    }}
                  >
                    {Math.abs(stats?.monthsCoveredCombined || 0) >= 1
                      ? `${(stats?.monthsCoveredCombined || 0).toFixed(2)} months`
                      : `${((stats?.monthsCoveredCombined || 0) * 30).toFixed(2)} days`}
                  </span>{" "}
                  (combined).
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* REAL DASHBOARD */}
          <Card style={cardStyle}>
            <CardContent>
              <Box
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "8px",
                  flexWrap: "wrap",
                  gap: "8px",
                }}
              >
                <Typography variant="h6" style={{ fontWeight: 600 }}>
                  Portfolio (Actual)
                </Typography>
                {realBalances?.exchangeRateFrom &&
                  realBalances?.exchangeRateFrom !==
                    realBalances?.exchangeRateTo && (
                    <Typography
                      variant="caption"
                      style={{
                        fontStyle: "italic",
                        color: isDark ? "#9ca3af" : "#6b7280",
                      }}
                    >
                      {realBalances?.exchangeRate
                        ? `1 ${realBalances.exchangeRateFrom} = ${realBalances.exchangeRate.toFixed(4)} ${realBalances.exchangeRateTo}`
                        : `Exchange rate unavailable (1 ${realBalances.exchangeRateFrom} = 1 ${realBalances.exchangeRateTo} fallback)`}
                    </Typography>
                  )}
              </Box>

              <Box
                style={{
                  padding: "20px",
                  backgroundColor: isDark ? "#1a1b1e" : "#f5f5f5",
                  borderRadius: "8px",
                  border: isDark ? "1px solid #373A40" : "1px solid #e0e0e0",
                }}
              >
                <Tooltip
                  title={
                    realBalances?.aumByCurrency &&
                    Object.keys(realBalances.aumByCurrency).length > 0
                      ? `${Object.entries(realBalances.aumByCurrency)
                          .filter(([currency, amount]) => amount > 0)
                          .map(
                            ([currency, amount]) =>
                              `${getCurrencySymbol(currency)}${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`,
                          )
                          .join(
                            " + ",
                          )} → ${getCurrencySymbol(stats?.baseCurrency || "AUD")}${stats?.baseCurrency || "AUD"}`
                      : ""
                  }
                  arrow
                  placement="right"
                >
                  <Typography
                    variant="body2"
                    style={{
                      marginBottom: "12px",
                      fontSize: "15px",
                      cursor: "pointer",
                      display: "inline-block",
                    }}
                  >
                    <strong>Total AUM after profit splits:</strong>{" "}
                    {getCurrencySymbol(stats?.baseCurrency || "AUD")}
                    {realStats.totalAUM.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                </Tooltip>

                <Typography
                  variant="body2"
                  style={{
                    marginBottom: "12px",
                    fontWeight: 600,
                    fontSize: "15px",
                  }}
                >
                  <strong>Total Monthly Net Profit:</strong>{" "}
                  <span
                    style={{
                      color:
                        realStats.totalNetProfitLoss === 0
                          ? "#ED9E0E"
                          : realStats.totalNetProfitLoss > 0
                            ? "#26a65d"
                            : "#f23645",
                    }}
                  >
                    {realStats.totalNetProfitLoss > 0
                      ? "+"
                      : realStats.totalNetProfitLoss < 0
                        ? ""
                        : ""}
                    {getCurrencySymbol(stats?.baseCurrency || "AUD")}
                    {realStats.totalNetProfitLoss.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </Typography>

                <Typography
                  variant="body2"
                  style={{
                    marginBottom: "12px",
                    fontSize: "15px",
                  }}
                >
                  <strong>Intraday Results:</strong>{" "}
                  <span
                    style={{
                      color:
                        realStats.dayTradingProfit === 0
                          ? "#ED9E0E"
                          : realStats.dayTradingProfit > 0
                            ? "#26a65d"
                            : "#f23645",
                    }}
                  >
                    {realStats.dayTradingProfit > 0
                      ? "+"
                      : realStats.dayTradingProfit < 0
                        ? ""
                        : ""}
                    {getCurrencySymbol(stats?.baseCurrency || "AUD")}
                    {realStats.dayTradingProfit.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                  , or{" "}
                  <span
                    style={{
                      color:
                        realStats.dayTradingPercentage === 0
                          ? "#ED9E0E"
                          : realStats.dayTradingPercentage > 0
                            ? "#26a65d"
                            : "#f23645",
                    }}
                  >
                    {realStats.dayTradingPercentage.toFixed(2)}%
                  </span>{" "}
                  of total.
                </Typography>

                <Typography
                  variant="body2"
                  style={{
                    marginBottom: "12px",
                    fontSize: "15px",
                  }}
                >
                  <strong>Swing Results:</strong>{" "}
                  <span
                    style={{
                      color:
                        realStats.swingTradingProfit === 0
                          ? "#ED9E0E"
                          : realStats.swingTradingProfit > 0
                            ? "#26a65d"
                            : "#f23645",
                    }}
                  >
                    {realStats.swingTradingProfit > 0
                      ? "+"
                      : realStats.swingTradingProfit < 0
                        ? ""
                        : ""}
                    {getCurrencySymbol(stats?.baseCurrency || "AUD")}
                    {realStats.swingTradingProfit.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                  , or{" "}
                  <span
                    style={{
                      color:
                        realStats.swingTradingPercentage === 0
                          ? "#ED9E0E"
                          : realStats.swingTradingPercentage > 0
                            ? "#26a65d"
                            : "#f23645",
                    }}
                  >
                    {realStats.swingTradingPercentage.toFixed(2)}%
                  </span>{" "}
                  of total.
                </Typography>

                <Divider
                  style={{
                    margin: "16px 0",
                    backgroundColor: isDark ? "#373A40" : "#e0e0e0",
                  }}
                />

                <Divider
                  style={{
                    margin: "16px 0",
                    backgroundColor: isDark ? "#373A40" : "#e0e0e0",
                  }}
                />

                <Typography
                  variant="body2"
                  style={{ marginBottom: "12px", fontSize: "15px" }}
                >
                  <strong>Outside Income:</strong>{" "}
                  <span style={{ color: "#2196f3" }}>
                    {getCurrencySymbol(stats?.baseCurrency || "AUD")}
                    {(stats?.totalOutsideIncome || 0).toLocaleString(
                      undefined,
                      {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      },
                    )}
                  </span>
                </Typography>

                <Typography
                  variant="body2"
                  style={{
                    marginBottom: "12px",
                    fontWeight: 600,
                    fontSize: "15px",
                  }}
                >
                  <strong>Total Combined Income:</strong>{" "}
                  <span
                    style={{
                      color:
                        realStats.totalNetProfitLoss +
                          (stats?.totalOutsideIncome || 0) ===
                        0
                          ? "#ED9E0E"
                          : realStats.totalNetProfitLoss +
                                (stats?.totalOutsideIncome || 0) >
                              0
                            ? "#26a65d"
                            : "#f23645",
                    }}
                  >
                    {realStats.totalNetProfitLoss +
                      (stats?.totalOutsideIncome || 0) >
                    0
                      ? "+"
                      : realStats.totalNetProfitLoss +
                            (stats?.totalOutsideIncome || 0) <
                          0
                        ? ""
                        : ""}
                    {getCurrencySymbol(stats?.baseCurrency || "AUD")}
                    {(
                      realStats.totalNetProfitLoss +
                      (stats?.totalOutsideIncome || 0)
                    ).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </Typography>

                <Divider
                  style={{
                    margin: "16px 0",
                    backgroundColor: isDark ? "#373A40" : "#e0e0e0",
                  }}
                />

                <Typography
                  variant="body2"
                  style={{
                    marginBottom: "8px",
                    fontStyle: "italic",
                    fontSize: "15px",
                  }}
                >
                  Based on monthly expenses of{" "}
                  <span
                    style={{
                      color:
                        (stats?.totalExpenses || 0) === 0
                          ? "#ED9E0E"
                          : "#f23645",
                    }}
                  >
                    {getCurrencySymbol(stats?.baseCurrency || "AUD")}
                    {(stats?.totalExpenses || 0).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                  :
                </Typography>

                <Typography
                  variant="body2"
                  style={{ marginBottom: "8px", fontSize: "15px" }}
                >
                  My expenses are{" "}
                  <span
                    style={{
                      color:
                        (stats?.totalExpenses || 0) > 0
                          ? ((stats?.totalExpenses || 0) /
                              (realStats.totalNetProfitLoss || 1)) *
                              100 ===
                            100
                            ? "#ED9E0E"
                            : ((stats?.totalExpenses || 0) /
                                  (realStats.totalNetProfitLoss || 1)) *
                                  100 <
                                100
                              ? "#26a65d"
                              : "#f23645"
                          : "#ED9E0E",
                    }}
                  >
                    {(realStats.totalNetProfitLoss || 0) !== 0
                      ? `${(((stats?.totalExpenses || 0) / realStats.totalNetProfitLoss) * 100).toFixed(2)}%`
                      : "0.00%"}
                  </span>{" "}
                  of trading profit, or{" "}
                  <span
                    style={{
                      color:
                        (stats?.totalExpenses || 0) > 0
                          ? ((stats?.totalExpenses || 0) /
                              (realStats.totalNetProfitLoss +
                                (stats?.totalOutsideIncome || 0) || 1)) *
                              100 ===
                            100
                            ? "#ED9E0E"
                            : ((stats?.totalExpenses || 0) /
                                  (realStats.totalNetProfitLoss +
                                    (stats?.totalOutsideIncome || 0) || 1)) *
                                  100 <
                                100
                              ? "#26a65d"
                              : "#f23645"
                          : "#ED9E0E",
                    }}
                  >
                    {(realStats.totalNetProfitLoss +
                      (stats?.totalOutsideIncome || 0) || 0) !== 0
                      ? `${(((stats?.totalExpenses || 0) / (realStats.totalNetProfitLoss + (stats?.totalOutsideIncome || 0))) * 100).toFixed(2)}%`
                      : "0.00%"}
                  </span>{" "}
                  of combined income.
                </Typography>

                <Typography variant="body2" style={{ fontSize: "15px" }}>
                  My monthly returns cover{" "}
                  <span
                    style={{
                      color:
                        (stats?.totalExpenses || 0) > 0
                          ? realStats.totalNetProfitLoss /
                              (stats?.totalExpenses || 1) ===
                            1
                            ? "#ED9E0E"
                            : realStats.totalNetProfitLoss /
                                  (stats?.totalExpenses || 1) >=
                                1
                              ? "#26a65d"
                              : "#f23645"
                          : "#ED9E0E",
                    }}
                  >
                    {(stats?.totalExpenses || 0) > 0
                      ? Math.abs(
                          realStats.totalNetProfitLoss /
                            (stats?.totalExpenses || 1),
                        ) >= 1
                        ? `${(realStats.totalNetProfitLoss / (stats?.totalExpenses || 1)).toFixed(2)} months`
                        : `${((realStats.totalNetProfitLoss / (stats?.totalExpenses || 1)) * 30).toFixed(2)} days`
                      : "0.0 days"}
                  </span>{" "}
                  (trading only), or{" "}
                  <span
                    style={{
                      color:
                        (stats?.totalExpenses || 0) > 0
                          ? (realStats.totalNetProfitLoss +
                              (stats?.totalOutsideIncome || 0)) /
                              (stats?.totalExpenses || 1) ===
                            1
                            ? "#ED9E0E"
                            : (realStats.totalNetProfitLoss +
                                  (stats?.totalOutsideIncome || 0)) /
                                  (stats?.totalExpenses || 1) >=
                                1
                              ? "#26a65d"
                              : "#f23645"
                          : "#ED9E0E",
                    }}
                  >
                    {(stats?.totalExpenses || 0) > 0
                      ? Math.abs(
                          (realStats.totalNetProfitLoss +
                            (stats?.totalOutsideIncome || 0)) /
                            (stats?.totalExpenses || 1),
                        ) >= 1
                        ? `${((realStats.totalNetProfitLoss + (stats?.totalOutsideIncome || 0)) / (stats?.totalExpenses || 1)).toFixed(2)} months`
                        : `${(((realStats.totalNetProfitLoss + (stats?.totalOutsideIncome || 0)) / (stats?.totalExpenses || 1)) * 30).toFixed(2)} days`
                      : "0.0 days"}
                  </span>{" "}
                  (combined).
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* ============ SECTION 3: PORTFOLIO & EXPENSES TABLES ============ */}
        <Card style={cardStyle}>
          <CardContent>
            <Box
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: width < 768 ? "flex-start" : "center",
                marginBottom: "16px",
                flexDirection: width < 768 ? "column" : "row",
                gap: width < 768 ? "12px" : "0",
              }}
            >
              <Box
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  width: width < 768 ? "100%" : "auto",
                  overflowX: width < 768 ? "auto" : "visible",
                }}
              >
                <Tabs
                  value={activeTab}
                  onChange={(e, newValue) => {
                    setActiveTab(newValue);
                    // Tab 0 = Portfolio
                    // Tab 1 = Outside Income
                    // Tabs 2+ = Expense groups
                    if (newValue >= 2) {
                      const groupIndex = newValue - 2;
                      if (groupIndex < expenseGroups.length) {
                        setActiveExpenseGroup(expenseGroups[groupIndex].id);
                      }
                    }
                  }}
                  variant={width < 768 ? "scrollable" : "standard"}
                  scrollButtons={width < 768 ? "auto" : false}
                  allowScrollButtonsMobile
                  style={{
                    borderBottom: isDark
                      ? "1px solid #373A40"
                      : "1px solid #e0e0e0",
                    flex: 1,
                    minWidth: 0,
                  }}
                  TabIndicatorProps={{
                    style: { backgroundColor: "#26a65d" },
                  }}
                >
                  <Tab
                    label="Portfolio (Projected)"
                    style={{
                      color: isDark ? "#FCFCFF" : "#1a1b1e",
                      textTransform: "none",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  />
                  <Tab
                    label="Outside Income"
                    style={{
                      color: isDark ? "#FCFCFF" : "#1a1b1e",
                      textTransform: "none",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  />
                  {expenseGroups.map((group, index) => (
                    <Tab
                      key={group.id}
                      label={
                        <Box
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                          onDoubleClick={(e) => {
                            e.stopPropagation();
                            setEditingGroupId(group.id);
                            setEditingGroupName(group.name);
                          }}
                        >
                          {editingGroupId === group.id ? (
                            <input
                              type="text"
                              value={editingGroupName}
                              onChange={(e) =>
                                setEditingGroupName(e.target.value)
                              }
                              onBlur={() =>
                                handleRenameExpenseGroup(
                                  group.id,
                                  editingGroupName,
                                )
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  handleRenameExpenseGroup(
                                    group.id,
                                    editingGroupName,
                                  );
                                } else if (e.key === "Escape") {
                                  setEditingGroupId(null);
                                  setEditingGroupName("");
                                }
                              }}
                              autoFocus
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                background: "transparent",
                                border: "1px solid #26a65d",
                                borderRadius: "4px",
                                padding: "2px 6px",
                                color: isDark ? "#FCFCFF" : "#1a1b1e",
                                fontSize: "14px",
                                fontWeight: 600,
                                outline: "none",
                                width: "120px",
                              }}
                            />
                          ) : (
                            <span>{group.name}</span>
                          )}
                          {group.id !== "default" &&
                            editingGroupId !== group.id && (
                              <CloseIcon
                                fontSize="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteExpenseGroup(group.id);
                                }}
                                style={{
                                  cursor: "pointer",
                                  fontSize: "16px",
                                }}
                              />
                            )}
                        </Box>
                      }
                      style={{
                        color: isDark ? "#FCFCFF" : "#1a1b1e",
                        textTransform: "none",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    />
                  ))}
                </Tabs>
                <IconButton
                  onClick={handleCreateExpenseGroup}
                  disabled={expenseGroups.length >= 5}
                  style={{
                    backgroundColor:
                      expenseGroups.length >= 5
                        ? isDark
                          ? "#555"
                          : "#ccc"
                        : isDark
                          ? "#4CAF50"
                          : "#26a65d",
                    color: "#fff",
                    padding: "8px",
                    width: "36px",
                    height: "36px",
                    cursor:
                      expenseGroups.length >= 5 ? "not-allowed" : "pointer",
                    opacity: expenseGroups.length >= 5 ? 0.5 : 1,
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={
                  activeTab === 0
                    ? handleOpenAccountCreate
                    : activeTab === 1
                      ? handleOpenOutsideIncomeCreate
                      : handleOpenExpenseCreate
                }
                style={{
                  backgroundColor:
                    activeTab === 0
                      ? "#26a65d"
                      : activeTab === 1
                        ? "#2196f3"
                        : "#2196f3",
                  color: "#ffffff",
                  textTransform: "none",
                  cursor: "pointer",
                  width: width < 768 ? "100%" : "auto",
                }}
              >
                {activeTab === 0
                  ? "Add Account"
                  : activeTab === 1
                    ? "Add Outside Income"
                    : "Add Expense"}
              </Button>
            </Box>

            {activeTab === 0 &&
              (width < 768 ? (
                // Mobile Card View
                <Box>
                  {accounts.length === 0 ? (
                    <Box
                      style={{
                        padding: "24px",
                        textAlign: "center",
                        color: isDark ? "#9ca3af" : "#6b7280",
                      }}
                    >
                      No accounts yet. Click "Add Account" to get started.
                    </Box>
                  ) : (
                    <>
                      {accounts.map((account, index) => (
                        <Card
                          key={account._id}
                          style={{
                            ...cardStyle,
                            marginBottom: "16px",
                            padding: "16px",
                          }}
                        >
                          <Box
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              marginBottom: "12px",
                            }}
                          >
                            <Typography
                              variant="h6"
                              style={{ fontWeight: 600, fontSize: "1.1rem" }}
                            >
                              {index + 1}. {account.name}
                            </Typography>
                            <Box>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenAccountEdit(account)}
                                style={{
                                  color: isDark ? "#FCFCFF" : "#1a1b1e",
                                  cursor: "pointer",
                                  padding: "4px",
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteAccount(account)}
                                style={{
                                  color: "#f23645",
                                  cursor: "pointer",
                                  padding: "4px",
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                          <Divider
                            style={{
                              margin: "8px 0",
                              backgroundColor: isDark ? "#373A40" : "#e0e0e0",
                            }}
                          />
                          <Box
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: "12px",
                              fontSize: "0.9rem",
                            }}
                          >
                            <Box>
                              <Typography
                                variant="body2"
                                style={{
                                  color: isDark ? "#9ca3af" : "#6b7280",
                                  fontSize: "0.75rem",
                                }}
                              >
                                Type
                              </Typography>
                              <Typography
                                variant="body2"
                                style={{ fontWeight: 500 }}
                              >
                                {account.type}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography
                                variant="body2"
                                style={{
                                  color: isDark ? "#9ca3af" : "#6b7280",
                                  fontSize: "0.75rem",
                                }}
                              >
                                Style
                              </Typography>
                              <Typography
                                variant="body2"
                                style={{ fontWeight: 500 }}
                              >
                                {account.style}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography
                                variant="body2"
                                style={{
                                  color: isDark ? "#9ca3af" : "#6b7280",
                                  fontSize: "0.75rem",
                                }}
                              >
                                Account Size
                              </Typography>
                              <Typography
                                variant="body2"
                                style={{ fontWeight: 500 }}
                              >
                                {getCurrencySymbol(account.currency || "USD")}
                                {account.accountSize.toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography
                                variant="body2"
                                style={{
                                  color: isDark ? "#9ca3af" : "#6b7280",
                                  fontSize: "0.75rem",
                                }}
                              >
                                Currency
                              </Typography>
                              <Typography
                                variant="body2"
                                style={{ fontWeight: 500 }}
                              >
                                {account.currency || "USD"}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography
                                variant="body2"
                                style={{
                                  color: isDark ? "#9ca3af" : "#6b7280",
                                  fontSize: "0.75rem",
                                }}
                              >
                                Max % Per Payout
                              </Typography>
                              <Typography
                                variant="body2"
                                style={{ fontWeight: 500 }}
                              >
                                {account.maxPercentPerPayout}%
                              </Typography>
                            </Box>
                            <Box>
                              <Typography
                                variant="body2"
                                style={{
                                  color: isDark ? "#9ca3af" : "#6b7280",
                                  fontSize: "0.75rem",
                                }}
                              >
                                Profit Split
                              </Typography>
                              <Typography
                                variant="body2"
                                style={{ fontWeight: 500 }}
                              >
                                {account.profitSplit}%
                              </Typography>
                            </Box>
                          </Box>
                          <Divider
                            style={{
                              margin: "12px 0",
                              backgroundColor: isDark ? "#373A40" : "#e0e0e0",
                            }}
                          />
                          <Box style={{ textAlign: "center" }}>
                            <Typography
                              variant="body2"
                              style={{
                                color: isDark ? "#9ca3af" : "#6b7280",
                                fontSize: "0.75rem",
                                marginBottom: "4px",
                              }}
                            >
                              Monthly Net Profit
                            </Typography>
                            <Typography
                              variant="h6"
                              style={{ color: "#26a65d", fontWeight: 600 }}
                            >
                              {getCurrencySymbol(account.currency || "USD")}
                              {calculateMonthlyNetProfit(
                                account,
                              ).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </Typography>
                          </Box>
                        </Card>
                      ))}
                      <Card
                        style={{
                          ...cardStyle,
                          padding: "16px",
                          backgroundColor: isDark ? "#1a1b1e" : "#f5f5f5",
                        }}
                      >
                        <Typography
                          variant="h6"
                          style={{
                            fontWeight: 600,
                            color: "#26a65d",
                            textAlign: "center",
                          }}
                        >
                          Total:{" "}
                          {getCurrencySymbol(stats?.baseCurrency || "AUD")}
                          {(stats?.totalMonthlyNetProfit || 0).toLocaleString(
                            undefined,
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            },
                          )}{" "}
                          {stats?.baseCurrency || "AUD"}
                        </Typography>
                      </Card>
                    </>
                  )}
                </Box>
              ) : (
                // Desktop Table View
                <TableContainer component={Paper} style={tableContainerStyle}>
                  <Table size="medium">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          style={{ ...tableCellStyle, fontWeight: 600 }}
                          align="center"
                        >
                          #
                        </TableCell>
                        <TableCell
                          style={{ ...tableCellStyle, fontWeight: 600 }}
                          align="center"
                        >
                          Name
                        </TableCell>
                        <TableCell
                          style={{ ...tableCellStyle, fontWeight: 600 }}
                          align="center"
                        >
                          Type
                        </TableCell>
                        <TableCell
                          style={{ ...tableCellStyle, fontWeight: 600 }}
                          align="center"
                        >
                          Account Size
                        </TableCell>
                        <TableCell
                          style={{ ...tableCellStyle, fontWeight: 600 }}
                          align="center"
                        >
                          Currency
                        </TableCell>
                        <TableCell
                          style={{ ...tableCellStyle, fontWeight: 600 }}
                          align="center"
                        >
                          Style
                        </TableCell>
                        <TableCell
                          style={{ ...tableCellStyle, fontWeight: 600 }}
                          align="center"
                        >
                          Max % Per Payout
                        </TableCell>
                        <TableCell
                          style={{ ...tableCellStyle, fontWeight: 600 }}
                          align="center"
                        >
                          Profit Split
                        </TableCell>
                        <TableCell
                          style={{ ...tableCellStyle, fontWeight: 600 }}
                          align="center"
                        >
                          Monthly Net Profit
                        </TableCell>
                        <TableCell
                          style={{ ...tableCellStyle, fontWeight: 600 }}
                          align="center"
                        >
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {accounts.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={10}
                            style={tableCellStyle}
                            align="center"
                          >
                            No accounts yet. Click "Add Account" to get started.
                          </TableCell>
                        </TableRow>
                      ) : (
                        accounts.map((account, index) => (
                          <TableRow key={account._id}>
                            <TableCell style={tableCellStyle} align="center">
                              {index + 1}
                            </TableCell>
                            <TableCell style={tableCellStyle} align="center">
                              {account.name}
                            </TableCell>
                            <TableCell style={tableCellStyle} align="center">
                              {account.type}
                            </TableCell>
                            <TableCell style={tableCellStyle} align="center">
                              {getCurrencySymbol(account.currency || "USD")}
                              {account.accountSize.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </TableCell>
                            <TableCell style={tableCellStyle} align="center">
                              {account.currency || "USD"}
                            </TableCell>
                            <TableCell style={tableCellStyle} align="center">
                              {account.style}
                            </TableCell>
                            <TableCell style={tableCellStyle} align="center">
                              {account.maxPercentPerPayout}%
                            </TableCell>
                            <TableCell style={tableCellStyle} align="center">
                              {account.profitSplit}%
                            </TableCell>
                            <TableCell
                              style={{
                                ...tableCellStyle,
                                color: "#26a65d",
                                fontWeight: 600,
                              }}
                              align="center"
                            >
                              {getCurrencySymbol(account.currency || "USD")}
                              {calculateMonthlyNetProfit(
                                account,
                              ).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </TableCell>
                            <TableCell style={tableCellStyle} align="center">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenAccountEdit(account)}
                                style={{
                                  color: isDark ? "#FCFCFF" : "#1a1b1e",
                                  cursor: "pointer",
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteAccount(account)}
                                style={{ color: "#f23645", cursor: "pointer" }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                      {accounts.length > 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={8}
                            style={{
                              ...tableCellStyle,
                              fontWeight: 600,
                              fontSize: "1rem",
                            }}
                            align="right"
                          ></TableCell>
                          <TableCell
                            colSpan={2}
                            style={{
                              ...tableCellStyle,
                              fontWeight: 600,
                              fontSize: "1rem",
                              color: "#26a65d",
                            }}
                            align="right"
                          >
                            <Tooltip
                              title={
                                stats?.monthlyNetProfitByCurrency &&
                                Object.keys(stats.monthlyNetProfitByCurrency)
                                  .length > 0
                                  ? `${Object.entries(
                                      stats.monthlyNetProfitByCurrency,
                                    )
                                      .filter(
                                        ([currency, amount]) => amount > 0,
                                      )
                                      .map(
                                        ([currency, amount]) =>
                                          `${getCurrencySymbol(currency)}${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`,
                                      )
                                      .join(
                                        " + ",
                                      )} → ${getCurrencySymbol(stats?.baseCurrency || "AUD")}${stats?.baseCurrency || "AUD"}`
                                  : ""
                              }
                              arrow
                              placement="left"
                            >
                              <Typography
                                variant="body1"
                                style={{
                                  fontWeight: 600,
                                  color: "#26a65d",
                                  cursor: "pointer",
                                  fontSize: "1.1rem",
                                }}
                              >
                                Total:{" "}
                                {getCurrencySymbol(
                                  stats?.baseCurrency || "AUD",
                                )}
                                {(
                                  stats?.totalMonthlyNetProfit || 0
                                ).toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}{" "}
                                {stats?.baseCurrency || "AUD"}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              ))}

            {activeTab === 1 &&
              (width < 768 ? (
                // Mobile Card View
                <Box>
                  {outsideIncomes.length === 0 ? (
                    <Box
                      style={{
                        padding: "24px",
                        textAlign: "center",
                        color: isDark ? "#9ca3af" : "#6b7280",
                      }}
                    >
                      Add Outside Income Sources.
                    </Box>
                  ) : (
                    <>
                      {outsideIncomes.map((income, index) => {
                        const total = outsideIncomes.reduce(
                          (sum, inc) => sum + inc.amount,
                          0,
                        );
                        const percentage =
                          total > 0 ? (income.amount / total) * 100 : 0;

                        return (
                          <Card
                            key={income._id}
                            style={{
                              ...cardStyle,
                              marginBottom: "16px",
                              padding: "16px",
                            }}
                          >
                            <Box
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                marginBottom: "12px",
                              }}
                            >
                              <Typography
                                variant="h6"
                                style={{ fontWeight: 600, fontSize: "1.1rem" }}
                              >
                                {index + 1}. {income.name}
                              </Typography>
                              <Box>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleOpenOutsideIncomeEdit(income)
                                  }
                                  style={{
                                    color: isDark ? "#FCFCFF" : "#1a1b1e",
                                    cursor: "pointer",
                                    padding: "4px",
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleDeleteOutsideIncome(income)
                                  }
                                  style={{
                                    color: "#f23645",
                                    cursor: "pointer",
                                    padding: "4px",
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                            <Divider
                              style={{
                                margin: "8px 0",
                                backgroundColor: isDark ? "#373A40" : "#e0e0e0",
                              }}
                            />
                            <Box
                              style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: "12px",
                                fontSize: "0.9rem",
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="body2"
                                  style={{
                                    color: isDark ? "#9ca3af" : "#6b7280",
                                    fontSize: "0.75rem",
                                  }}
                                >
                                  Monthly Amount
                                </Typography>
                                <Typography
                                  variant="body2"
                                  style={{ fontWeight: 600, color: "#2196f3" }}
                                >
                                  {getCurrencySymbol(income.currency || "USD")}
                                  {income.amount.toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography
                                  variant="body2"
                                  style={{
                                    color: isDark ? "#9ca3af" : "#6b7280",
                                    fontSize: "0.75rem",
                                  }}
                                >
                                  Currency
                                </Typography>
                                <Typography
                                  variant="body2"
                                  style={{ fontWeight: 500 }}
                                >
                                  {income.currency || "USD"}
                                </Typography>
                              </Box>
                              <Box style={{ gridColumn: "1 / -1" }}>
                                <Typography
                                  variant="body2"
                                  style={{
                                    color: isDark ? "#9ca3af" : "#6b7280",
                                    fontSize: "0.75rem",
                                  }}
                                >
                                  % of Total
                                </Typography>
                                <Typography
                                  variant="body2"
                                  style={{
                                    fontWeight: 600,
                                    color:
                                      percentage === 0 ? "#ED9E0E" : "#2196f3",
                                  }}
                                >
                                  {percentage.toFixed(2)}%
                                </Typography>
                              </Box>
                            </Box>
                          </Card>
                        );
                      })}
                      <Card
                        style={{
                          ...cardStyle,
                          padding: "16px",
                          backgroundColor: isDark ? "#1a1b1e" : "#f5f5f5",
                        }}
                      >
                        <Typography
                          variant="h6"
                          style={{
                            fontWeight: 600,
                            color: "#26a65d",
                            textAlign: "center",
                          }}
                        >
                          Total: {getCurrencySymbol(appBaseCurrency)}
                          {outsideIncomes
                            .reduce((sum, inc) => sum + inc.amount, 0)
                            .toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}{" "}
                          {appBaseCurrency}
                        </Typography>
                      </Card>
                    </>
                  )}
                </Box>
              ) : (
                // Desktop Table View
                <TableContainer component={Paper} style={tableContainerStyle}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          style={{ ...tableCellStyle, fontWeight: 600 }}
                          align="center"
                        >
                          #
                        </TableCell>
                        <TableCell
                          style={{ ...tableCellStyle, fontWeight: 600 }}
                          align="center"
                        >
                          Income Source
                        </TableCell>
                        <TableCell
                          style={{ ...tableCellStyle, fontWeight: 600 }}
                          align="center"
                        >
                          Monthly Amount
                        </TableCell>
                        <TableCell
                          style={{ ...tableCellStyle, fontWeight: 600 }}
                          align="center"
                        >
                          Currency
                        </TableCell>
                        <TableCell
                          style={{ ...tableCellStyle, fontWeight: 600 }}
                          align="center"
                        >
                          % of Total
                        </TableCell>
                        <TableCell
                          style={{ ...tableCellStyle, fontWeight: 600 }}
                          align="center"
                        >
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {outsideIncomes.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            style={tableCellStyle}
                            align="center"
                          >
                            Add Outside Income Sources.
                          </TableCell>
                        </TableRow>
                      ) : (
                        outsideIncomes.map((income, index) => {
                          const total = outsideIncomes.reduce(
                            (sum, inc) => sum + inc.amount,
                            0,
                          );
                          const percentage =
                            total > 0 ? (income.amount / total) * 100 : 0;

                          return (
                            <TableRow key={income._id}>
                              <TableCell style={tableCellStyle} align="center">
                                {index + 1}
                              </TableCell>
                              <TableCell style={tableCellStyle} align="center">
                                {income.name}
                              </TableCell>
                              <TableCell style={tableCellStyle} align="center">
                                {getCurrencySymbol(income.currency || "USD")}
                                {income.amount.toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </TableCell>
                              <TableCell style={tableCellStyle} align="center">
                                {income.currency || "USD"}
                              </TableCell>
                              <TableCell style={tableCellStyle} align="center">
                                <span
                                  style={{
                                    color:
                                      percentage === 0 ? "#ED9E0E" : "#2196f3",
                                    fontWeight: 600,
                                  }}
                                >
                                  {percentage.toFixed(2)}%
                                </span>
                              </TableCell>
                              <TableCell style={tableCellStyle} align="center">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleOpenOutsideIncomeEdit(income)
                                  }
                                  style={{
                                    color: isDark ? "#FCFCFF" : "#1a1b1e",
                                    cursor: "pointer",
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleDeleteOutsideIncome(income)
                                  }
                                  style={{
                                    color: "#f23645",
                                    cursor: "pointer",
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                      {outsideIncomes.length > 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={4}
                            style={{
                              ...tableCellStyle,
                              fontWeight: 600,
                              fontSize: "1rem",
                            }}
                            align="right"
                          ></TableCell>
                          <TableCell
                            colSpan={2}
                            style={{
                              ...tableCellStyle,
                              fontWeight: 600,
                              fontSize: "1rem",
                              color: "#26a65d",
                            }}
                            align="right"
                          >
                            <Typography
                              variant="body1"
                              style={{
                                fontWeight: 600,
                                color: "#26a65d",
                                fontSize: "1.1rem",
                              }}
                            >
                              Total: {getCurrencySymbol(appBaseCurrency)}
                              {outsideIncomes
                                .reduce((sum, inc) => sum + inc.amount, 0)
                                .toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}{" "}
                              {appBaseCurrency}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              ))}

            {activeTab >= 2 &&
              (width < 768 ? (
                // Mobile Card View
                <Box>
                  {activeGroupExpenses.length === 0 ? (
                    <Box
                      style={{
                        padding: "24px",
                        textAlign: "center",
                        color: isDark ? "#9ca3af" : "#6b7280",
                      }}
                    >
                      Enter Total Monthly Expenses Here 
                    </Box>
                  ) : (
                    <>
                      {activeGroupExpenses.map((expense, index) => {
                        const groupTotal = activeGroupExpenses.reduce(
                          (sum, exp) => sum + exp.amount,
                          0,
                        );
                        const percentage =
                          groupTotal > 0
                            ? (expense.amount / groupTotal) * 100
                            : 0;

                        return (
                          <Card
                            key={expense._id}
                            style={{
                              ...cardStyle,
                              marginBottom: "16px",
                              padding: "16px",
                            }}
                          >
                            <Box
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                marginBottom: "12px",
                              }}
                            >
                              <Typography
                                variant="h6"
                                style={{ fontWeight: 600, fontSize: "1.1rem" }}
                              >
                                {index + 1}. {expense.name}
                              </Typography>
                              <Box>
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenExpenseEdit(expense)}
                                  style={{
                                    color: isDark ? "#FCFCFF" : "#1a1b1e",
                                    cursor: "pointer",
                                    padding: "4px",
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteExpense(expense)}
                                  style={{
                                    color: "#f23645",
                                    cursor: "pointer",
                                    padding: "4px",
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                            <Divider
                              style={{
                                margin: "8px 0",
                                backgroundColor: isDark ? "#373A40" : "#e0e0e0",
                              }}
                            />
                            <Box
                              style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: "12px",
                                fontSize: "0.9rem",
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="body2"
                                  style={{
                                    color: isDark ? "#9ca3af" : "#6b7280",
                                    fontSize: "0.75rem",
                                  }}
                                >
                                  Amount
                                </Typography>
                                <Typography
                                  variant="body2"
                                  style={{ fontWeight: 600, color: "#f23645" }}
                                >
                                  {getCurrencySymbol(expense.currency || "USD")}
                                  {expense.amount.toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography
                                  variant="body2"
                                  style={{
                                    color: isDark ? "#9ca3af" : "#6b7280",
                                    fontSize: "0.75rem",
                                  }}
                                >
                                  Currency
                                </Typography>
                                <Typography
                                  variant="body2"
                                  style={{ fontWeight: 500 }}
                                >
                                  {expense.currency || "USD"}
                                </Typography>
                              </Box>
                              <Box style={{ gridColumn: "1 / -1" }}>
                                <Typography
                                  variant="body2"
                                  style={{
                                    color: isDark ? "#9ca3af" : "#6b7280",
                                    fontSize: "0.75rem",
                                  }}
                                >
                                  % of Total
                                </Typography>
                                <Typography
                                  variant="body2"
                                  style={{
                                    fontWeight: 600,
                                    color:
                                      percentage === 0 ? "#ED9E0E" : "#26a65d",
                                  }}
                                >
                                  {percentage.toFixed(2)}%
                                </Typography>
                              </Box>
                            </Box>
                          </Card>
                        );
                      })}
                      <Card
                        style={{
                          ...cardStyle,
                          padding: "16px",
                          backgroundColor: isDark ? "#1a1b1e" : "#f5f5f5",
                        }}
                      >
                        <Typography
                          variant="h6"
                          style={{
                            fontWeight: 600,
                            color: "#f23645",
                            textAlign: "center",
                          }}
                        >
                          Total: {getCurrencySymbol(appBaseCurrency)}
                          {activeGroupExpenses
                            .reduce((sum, exp) => sum + exp.amount, 0)
                            .toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}{" "}
                          {appBaseCurrency}
                        </Typography>
                      </Card>
                    </>
                  )}
                </Box>
              ) : (
                // Desktop Table View
                <TableContainer component={Paper} style={tableContainerStyle}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          style={{ ...tableCellStyle, fontWeight: 600 }}
                          align="center"
                        >
                          #
                        </TableCell>
                        <TableCell
                          style={{ ...tableCellStyle, fontWeight: 600 }}
                          align="center"
                        >
                          Expenses
                        </TableCell>
                        <TableCell
                          style={{ ...tableCellStyle, fontWeight: 600 }}
                          align="center"
                        >
                          Amount
                        </TableCell>
                        <TableCell
                          style={{ ...tableCellStyle, fontWeight: 600 }}
                          align="center"
                        >
                          Currency
                        </TableCell>
                        <TableCell
                          style={{ ...tableCellStyle, fontWeight: 600 }}
                          align="center"
                        >
                          % of Total
                        </TableCell>
                        <TableCell
                          style={{ ...tableCellStyle, fontWeight: 600 }}
                          align="center"
                        >
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {activeGroupExpenses.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            style={tableCellStyle}
                            align="center"
                          >
                             Enter Total Monthly Expenses Here 
                          </TableCell>
                        </TableRow>
                      ) : (
                        activeGroupExpenses.map((expense, index) => {
                          const groupTotal = activeGroupExpenses.reduce(
                            (sum, exp) => sum + exp.amount,
                            0,
                          );
                          const percentage =
                            groupTotal > 0
                              ? (expense.amount / groupTotal) * 100
                              : 0;

                          return (
                            <TableRow key={expense._id}>
                              <TableCell style={tableCellStyle} align="center">
                                {index + 1}
                              </TableCell>
                              <TableCell style={tableCellStyle} align="center">
                                {expense.name}
                              </TableCell>
                              <TableCell style={tableCellStyle} align="center">
                                {getCurrencySymbol(expense.currency || "USD")}
                                {expense.amount.toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </TableCell>
                              <TableCell style={tableCellStyle} align="center">
                                {expense.currency || "USD"}
                              </TableCell>
                              <TableCell style={tableCellStyle} align="center">
                                <span
                                  style={{
                                    color:
                                      percentage === 0 ? "#ED9E0E" : "#26a65d",
                                    fontWeight: 600,
                                  }}
                                >
                                  {percentage.toFixed(2)}%
                                </span>
                              </TableCell>
                              <TableCell style={tableCellStyle} align="center">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenExpenseEdit(expense)}
                                  style={{
                                    color: isDark ? "#FCFCFF" : "#1a1b1e",
                                    cursor: "pointer",
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteExpense(expense)}
                                  style={{
                                    color: "#f23645",
                                    cursor: "pointer",
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                      {activeGroupExpenses.length > 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={4}
                            style={{
                              ...tableCellStyle,
                              fontWeight: 600,
                              fontSize: "1rem",
                            }}
                            align="right"
                          ></TableCell>
                          <TableCell
                            colSpan={2}
                            style={{
                              ...tableCellStyle,
                              fontWeight: 600,
                              fontSize: "1rem",
                              color: "#f23645",
                            }}
                            align="right"
                          >
                            <Typography
                              variant="body1"
                              style={{
                                fontWeight: 600,
                                color: "#f23645",
                                fontSize: "1.1rem",
                              }}
                            >
                              Total: {getCurrencySymbol(appBaseCurrency)}
                              {activeGroupExpenses
                                .reduce((sum, exp) => sum + exp.amount, 0)
                                .toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}{" "}
                              {appBaseCurrency}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              ))}
          </CardContent>
        </Card>

        {/* ============ ACCOUNT DIALOG ============ */}
        <Dialog
          open={accountDialogOpen}
          onClose={() => setAccountDialogOpen(false)}
          sx={dialogStyle}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>
                {accountMode === "create" ? "Add Account" : "Edit Account"}
              </span>
              <IconButton
                onClick={() => setAccountDialogOpen(false)}
                style={{
                  color: isDark ? "#FCFCFF" : "#1a1b1e",
                  cursor: "pointer",
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box
              style={{
                display: "grid",
                gridTemplateColumns: width < 768 ? "1fr" : "1fr 1fr",
                gap: "16px",
                paddingTop: "8px",
              }}
            >
              <div>
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: "500",
                    color: isDark ? "#9ca3af" : "#6b7280",
                    display: "block",
                    marginBottom: "4px",
                  }}
                >
                  Account Type
                </span>
                <FormControl fullWidth>
                  <Select
                    size="small"
                    value={accountForm.type}
                    onChange={(e) =>
                      setAccountForm({ ...accountForm, type: e.target.value })
                    }
                    displayEmpty
                    MenuProps={{
                      sx: { zIndex: 99999 },
                      PaperProps: {
                        sx: {
                          bgcolor: isDark ? "rgba(40, 42, 46)" : "#FCFCFF",
                          maxHeight: "450px",
                          "& .MuiMenuItem-root": {
                            padding: 1.5,
                            justifyContent: "center",
                          },
                        },
                      },
                    }}
                    sx={{
                      height: "50px",
                      backgroundColor: isDark ? "#3a3d45" : "#e5e7eb",
                      color: isDark ? "#FCFCFF" : "#1f2937",
                      borderRadius: "12px",
                      fontSize: "14px",
                      fontWeight: 600,
                      ".MuiSvgIcon-root": {
                        fill: isDark ? "#FCFCFF" : "#1f2937",
                      },
                      "& fieldset": {
                        border: "none",
                      },
                      "&:hover": {
                        backgroundColor: isDark ? "#434650" : "#d1d5db",
                      },
                      "&:hover fieldset": {
                        border: "none",
                      },
                      "&.Mui-focused": {
                        backgroundColor: isDark ? "#3a3d45" : "#e5e7eb",
                      },
                      "&.Mui-focused fieldset": {
                        border: "none",
                      },
                      "& .MuiSelect-select": {
                        paddingTop: "12px",
                        paddingBottom: "12px",
                      },
                    }}
                  >
                    <MenuItem
                      value="Personal"
                      sx={{ color: isDark ? "#FCFCFF" : "rgba(40, 42, 46)" }}
                    >
                      Personal
                    </MenuItem>
                    <MenuItem
                      value="Tier 1 Prop Firm"
                      sx={{ color: isDark ? "#FCFCFF" : "rgba(40, 42, 46)" }}
                    >
                      Tier 1 Prop Firm
                    </MenuItem>
                    <MenuItem
                      value="Tier 2 Prop Firm"
                      sx={{ color: isDark ? "#FCFCFF" : "rgba(40, 42, 46)" }}
                    >
                      Tier 2 Prop Firm
                    </MenuItem>
                    <MenuItem
                      value="Tier 3 Prop Firm"
                      sx={{ color: isDark ? "#FCFCFF" : "rgba(40, 42, 46)" }}
                    >
                      Tier 3 Prop Firm
                    </MenuItem>
                  </Select>
                </FormControl>
              </div>

              <div>
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: "500",
                    color: isDark ? "#9ca3af" : "#6b7280",
                    display: "block",
                    marginBottom: "4px",
                  }}
                >
                  Style
                </span>
                <FormControl fullWidth>
                  <Select
                    size="small"
                    value={accountForm.style}
                    onChange={(e) => {
                      const newStyle = e.target.value;
                      setAccountForm({
                        ...accountForm,
                        style: newStyle,
                        dayTradingPercent:
                          newStyle === "Intraday"
                            ? "100"
                            : newStyle === "Swing"
                              ? "0"
                              : accountForm.dayTradingPercent,
                        swingTradingPercent:
                          newStyle === "Swing"
                            ? "100"
                            : newStyle === "Intraday"
                              ? "0"
                              : accountForm.swingTradingPercent,
                      });
                    }}
                    displayEmpty
                    MenuProps={{
                      sx: { zIndex: 99999 },
                      PaperProps: {
                        sx: {
                          bgcolor: isDark ? "rgba(40, 42, 46)" : "#FCFCFF",
                          maxHeight: "450px",
                          "& .MuiMenuItem-root": {
                            padding: 1.5,
                            justifyContent: "center",
                            color: isDark ? "#FCFCFF" : "rgba(40, 42, 46)",
                          },
                        },
                      },
                    }}
                    sx={{
                      height: "50px",
                      backgroundColor: isDark ? "#3a3d45" : "#e5e7eb",
                      color: isDark ? "#FCFCFF" : "#1f2937",
                      borderRadius: "12px",
                      fontSize: "14px",
                      fontWeight: 600,
                      ".MuiSvgIcon-root": {
                        fill: isDark ? "#FCFCFF" : "#1f2937",
                      },
                      "& fieldset": {
                        border: "none",
                      },
                      "&:hover": {
                        backgroundColor: isDark ? "#434650" : "#d1d5db",
                      },
                      "&:hover fieldset": {
                        border: "none",
                      },
                      "&.Mui-focused": {
                        backgroundColor: isDark ? "#3a3d45" : "#e5e7eb",
                      },
                      "&.Mui-focused fieldset": {
                        border: "none",
                      },
                      "& .MuiSelect-select": {
                        paddingTop: "12px",
                        paddingBottom: "12px",
                      },
                    }}
                  >
                    <MenuItem
                      value="Intraday"
                      sx={{ color: isDark ? "#FCFCFF" : "rgba(40, 42, 46)" }}
                    >
                      Intraday
                    </MenuItem>
                    <MenuItem
                      value="Swing"
                      sx={{ color: isDark ? "#FCFCFF" : "rgba(40, 42, 46)" }}
                    >
                      Swing
                    </MenuItem>
                    <MenuItem
                      value="Both"
                      sx={{ color: isDark ? "#FCFCFF" : "rgba(40, 42, 46)" }}
                    >
                      Both
                    </MenuItem>
                  </Select>
                </FormControl>
              </div>

              <div>
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: "500",
                    color: isDark ? "#9ca3af" : "#6b7280",
                    display: "block",
                    marginBottom: "4px",
                  }}
                >
                  Account Name
                </span>
                <CustomSingleTextField
                  type="text"
                  size="small"
                  value={accountForm.name}
                  onChange={(e) =>
                    setAccountForm({ ...accountForm, name: e.target.value })
                  }
                  placeholder="Account Name"
                />
              </div>

              <div>
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: "500",
                    color: isDark ? "#9ca3af" : "#6b7280",
                    display: "block",
                    marginBottom: "4px",
                  }}
                >
                  Account Size ($)
                </span>
                <CustomSingleTextField
                  type="number"
                  size="small"
                  value={accountForm.accountSize}
                  onChange={(e) =>
                    setAccountForm({
                      ...accountForm,
                      accountSize: e.target.value,
                    })
                  }
                  placeholder="Account Size"
                  inputProps={{
                    inputMode: "decimal",
                    pattern: "^[0-9]*[.]?[0-9]*$",
                  }}
                />
              </div>

              <div>
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: "500",
                    color: isDark ? "#9ca3af" : "#6b7280",
                    display: "block",
                    marginBottom: "4px",
                  }}
                >
                  Currency
                </span>
                <FormControl fullWidth>
                  <Select
                    size="small"
                    value={accountForm.currency}
                    onChange={(e) =>
                      setAccountForm({
                        ...accountForm,
                        currency: e.target.value,
                      })
                    }
                    displayEmpty
                    MenuProps={{
                      sx: { zIndex: 99999 },
                      PaperProps: {
                        sx: {
                          bgcolor: isDark ? "rgba(40, 42, 46)" : "#FCFCFF",
                          maxHeight: "450px",
                          "& .MuiMenuItem-root": {
                            padding: 1.5,
                            justifyContent: "center",
                          },
                        },
                      },
                    }}
                    sx={{
                      height: "50px",
                      backgroundColor: isDark ? "#3a3d45" : "#e5e7eb",
                      color: isDark ? "#FCFCFF" : "#1f2937",
                      borderRadius: "12px",
                      fontSize: "14px",
                      fontWeight: 600,
                      ".MuiSvgIcon-root": {
                        fill: isDark ? "#FCFCFF" : "#1f2937",
                      },
                      "& fieldset": {
                        border: "none",
                      },
                      "&:hover": {
                        backgroundColor: isDark ? "#434650" : "#d1d5db",
                      },
                      "&:hover fieldset": {
                        border: "none",
                      },
                      "&.Mui-focused": {
                        backgroundColor: isDark ? "#3a3d45" : "#e5e7eb",
                      },
                      "&.Mui-focused fieldset": {
                        border: "none",
                      },
                      "& .MuiSelect-select": {
                        paddingTop: "12px",
                        paddingBottom: "12px",
                      },
                    }}
                  >
                    <MenuItem
                      value="USD"
                      sx={{ color: isDark ? "#FCFCFF" : "rgba(40, 42, 46)" }}
                    >
                      USD ($)
                    </MenuItem>
                    <MenuItem
                      value="GBP"
                      sx={{ color: isDark ? "#FCFCFF" : "rgba(40, 42, 46)" }}
                    >
                      GBP (£)
                    </MenuItem>
                    <MenuItem
                      value="EUR"
                      sx={{ color: isDark ? "#FCFCFF" : "rgba(40, 42, 46)" }}
                    >
                      EUR (€)
                    </MenuItem>
                    <MenuItem
                      value="AUD"
                      sx={{ color: isDark ? "#FCFCFF" : "rgba(40, 42, 46)" }}
                    >
                      AUD (A$)
                    </MenuItem>
                    <MenuItem
                      value="NZD"
                      sx={{ color: isDark ? "#FCFCFF" : "rgba(40, 42, 46)" }}
                    >
                      NZD (NZ$)
                    </MenuItem>
                    <MenuItem
                      value="JPY"
                      sx={{ color: isDark ? "#FCFCFF" : "rgba(40, 42, 46)" }}
                    >
                      JPY (¥)
                    </MenuItem>
                    <MenuItem
                      value="CAD"
                      sx={{ color: isDark ? "#FCFCFF" : "rgba(40, 42, 46)" }}
                    >
                      CAD (C$)
                    </MenuItem>
                    <MenuItem
                      value="CHF"
                      sx={{ color: isDark ? "#FCFCFF" : "rgba(40, 42, 46)" }}
                    >
                      CHF (CHF)
                    </MenuItem>
                  </Select>
                </FormControl>
              </div>

              <div>
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: "500",
                    color: isDark ? "#9ca3af" : "#6b7280",
                    display: "block",
                    marginBottom: "4px",
                  }}
                >
                  Max % Per Payout
                </span>
                <CustomSingleTextField
                  type="number"
                  size="small"
                  value={accountForm.maxPercentPerPayout}
                  onChange={(e) =>
                    setAccountForm({
                      ...accountForm,
                      maxPercentPerPayout: e.target.value,
                    })
                  }
                  placeholder="Max % Per Payout"
                  inputProps={{
                    inputMode: "decimal",
                    pattern: "^[0-9]*[.]?[0-9]*$",
                  }}
                />
              </div>

              <div>
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: "500",
                    color: isDark ? "#9ca3af" : "#6b7280",
                    display: "block",
                    marginBottom: "4px",
                  }}
                >
                  Profit Split (%)
                </span>
                <CustomSingleTextField
                  type="number"
                  size="small"
                  value={accountForm.profitSplit}
                  onChange={(e) =>
                    setAccountForm({
                      ...accountForm,
                      profitSplit: e.target.value,
                    })
                  }
                  placeholder="Profit Split %"
                  inputProps={{
                    inputMode: "decimal",
                    pattern: "^[0-9]*[.]?[0-9]*$",
                  }}
                />
              </div>

              <div style={{ gridColumn: width < 768 ? "span 1" : "span 2" }}>
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: "500",
                    color: isDark ? "#9ca3af" : "#6b7280",
                    display: "block",
                    marginBottom: "4px",
                  }}
                >
                  Monthly Net Profit (Auto-calculated)
                </span>
                <Box
                  style={{
                    height: "50px",
                    backgroundColor: isDark ? "#1a1b1e" : "#f5f5f5",
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    paddingLeft: "16px",
                    fontSize: "16px",
                    fontWeight: 600,
                    color: "#26a65d",
                  }}
                >
                  $
                  {(() => {
                    const accountSize =
                      parseFloat(accountForm.accountSize) || 0;
                    const maxPercent =
                      parseFloat(accountForm.maxPercentPerPayout) || 0;
                    const profitSplit =
                      parseFloat(accountForm.profitSplit) || 0;
                    const maxPayout = (accountSize * maxPercent) / 100;
                    const afterSplit = (maxPayout * profitSplit) / 100;
                    return afterSplit.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    });
                  })()}
                </Box>
              </div>

              <Box
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    accountForm.style === "Both" ? "1fr 1fr" : "1fr",
                  gap: "16px",
                  gridColumn: width < 768 ? "span 1" : "span 2",
                }}
              >
                {(accountForm.style === "Intraday" ||
                  accountForm.style === "Both") && (
                  <div>
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: "500",
                        color: isDark ? "#9ca3af" : "#6b7280",
                        display: "block",
                        marginBottom: "4px",
                      }}
                    >
                      Day Trading %
                    </span>
                    {accountForm.style === "Intraday" ? (
                      <Box
                        style={{
                          height: "50px",
                          backgroundColor: isDark ? "#1a1b1e" : "#f5f5f5",
                          borderRadius: "12px",
                          display: "flex",
                          alignItems: "center",
                          paddingLeft: "16px",
                          fontSize: "16px",
                          fontWeight: 600,
                          color: isDark ? "#FCFCFF" : "#1a1b1e",
                        }}
                      >
                        100%
                      </Box>
                    ) : (
                      <CustomSingleTextField
                        type="number"
                        size="small"
                        value={accountForm.dayTradingPercent}
                        onChange={(e) => {
                          const newValue = parseFloat(e.target.value) || 0;
                          const cappedValue = Math.min(
                            Math.max(newValue, 0),
                            100,
                          );
                          setAccountForm({
                            ...accountForm,
                            dayTradingPercent: cappedValue.toString(),
                            swingTradingPercent: (100 - cappedValue).toString(),
                          });
                        }}
                        placeholder="Day Trading %"
                        inputProps={{
                          inputMode: "decimal",
                          pattern: "^[0-9]*[.]?[0-9]*$",
                          max: "100",
                        }}
                      />
                    )}
                  </div>
                )}

                {(accountForm.style === "Swing" ||
                  accountForm.style === "Both") && (
                  <div>
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: "500",
                        color: isDark ? "#9ca3af" : "#6b7280",
                        display: "block",
                        marginBottom: "4px",
                      }}
                    >
                      Swing Trading %
                    </span>
                    {accountForm.style === "Swing" ? (
                      <Box
                        style={{
                          height: "50px",
                          backgroundColor: isDark ? "#1a1b1e" : "#f5f5f5",
                          borderRadius: "12px",
                          display: "flex",
                          alignItems: "center",
                          paddingLeft: "16px",
                          fontSize: "16px",
                          fontWeight: 600,
                          color: isDark ? "#FCFCFF" : "#1a1b1e",
                        }}
                      >
                        100%
                      </Box>
                    ) : (
                      <CustomSingleTextField
                        type="number"
                        size="small"
                        value={accountForm.swingTradingPercent}
                        onChange={(e) => {
                          const newValue = parseFloat(e.target.value) || 0;
                          const cappedValue = Math.min(
                            Math.max(newValue, 0),
                            100,
                          );
                          setAccountForm({
                            ...accountForm,
                            swingTradingPercent: cappedValue.toString(),
                            dayTradingPercent: (100 - cappedValue).toString(),
                          });
                        }}
                        placeholder="Swing Trading %"
                        inputProps={{
                          inputMode: "decimal",
                          pattern: "^[0-9]*[.]?[0-9]*$",
                          max: "100",
                        }}
                      />
                    )}
                  </div>
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setAccountDialogOpen(false)}
              style={{
                color: isDark ? "#FCFCFF" : "#1a1b1e",
                cursor: "pointer",
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAccountSubmit}
              variant="contained"
              style={{
                backgroundColor: "#26a65d",
                color: "#ffffff",
                cursor: "pointer",
              }}
            >
              {accountMode === "create" ? "Create" : "Update"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ============ EXPENSE DIALOG ============ */}
        <Dialog
          open={expenseDialogOpen}
          onClose={() => setExpenseDialogOpen(false)}
          sx={dialogStyle}
        >
          <DialogTitle>
            <Box
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>
                {expenseMode === "create" ? "Add Expense" : "Edit Expense"}
              </span>
              <IconButton
                onClick={() => setExpenseDialogOpen(false)}
                style={{
                  color: isDark ? "#FCFCFF" : "#1a1b1e",
                  cursor: "pointer",
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box
              style={{
                display: "grid",
                gridTemplateColumns: width < 768 ? "1fr" : "1fr 1fr",
                gap: "16px",
                paddingTop: "8px",
              }}
            >
              <div>
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: "500",
                    color: isDark ? "#9ca3af" : "#6b7280",
                    display: "block",
                    marginBottom: "4px",
                  }}
                >
                  Expense Name
                </span>
                <CustomSingleTextField
                  type="text"
                  size="small"
                  value={expenseForm.name}
                  onChange={(e) =>
                    setExpenseForm({ ...expenseForm, name: e.target.value })
                  }
                  placeholder="Expense Name"
                />
              </div>

              <div>
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: "500",
                    color: isDark ? "#9ca3af" : "#6b7280",
                    display: "block",
                    marginBottom: "4px",
                  }}
                >
                  Amount ($)
                </span>
                <CustomSingleTextField
                  type="number"
                  size="small"
                  value={expenseForm.amount}
                  onChange={(e) =>
                    setExpenseForm({ ...expenseForm, amount: e.target.value })
                  }
                  placeholder="Amount"
                  inputProps={{
                    inputMode: "decimal",
                    pattern: "^[0-9]*[.]?[0-9]*$",
                  }}
                />
              </div>

              <div>
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: "500",
                    color: isDark ? "#9ca3af" : "#6b7280",
                    display: "block",
                    marginBottom: "4px",
                  }}
                >
                  Currency
                </span>
                <FormControl fullWidth>
                  <Select
                    size="small"
                    value={expenseForm.currency}
                    onChange={(e) =>
                      setExpenseForm({
                        ...expenseForm,
                        currency: e.target.value,
                      })
                    }
                    displayEmpty
                    MenuProps={{
                      sx: { zIndex: 99999 },
                      PaperProps: {
                        sx: {
                          bgcolor: isDark ? "rgba(40, 42, 46)" : "#FCFCFF",
                          maxHeight: "450px",
                          "& .MuiMenuItem-root": {
                            padding: 1.5,
                            justifyContent: "center",
                          },
                        },
                      },
                    }}
                    sx={{
                      height: "50px",
                      backgroundColor: isDark ? "#3a3d45" : "#e5e7eb",
                      color: isDark ? "#FCFCFF" : "#1f2937",
                      borderRadius: "12px",
                      fontSize: "14px",
                      fontWeight: 600,
                      ".MuiSvgIcon-root": {
                        fill: isDark ? "#FCFCFF" : "#1f2937",
                      },
                      "& fieldset": {
                        border: "none",
                      },
                      "&:hover": {
                        backgroundColor: isDark ? "#434650" : "#d1d5db",
                      },
                      "&:hover fieldset": {
                        border: "none",
                      },
                      "&.Mui-focused": {
                        backgroundColor: isDark ? "#3a3d45" : "#e5e7eb",
                      },
                      "&.Mui-focused fieldset": {
                        border: "none",
                      },
                      "& .MuiSelect-select": {
                        paddingTop: "12px",
                        paddingBottom: "12px",
                      },
                    }}
                  >
                    <MenuItem
                      value="USD"
                      sx={{ color: isDark ? "#FCFCFF" : "rgba(40, 42, 46)" }}
                    >
                      USD ($)
                    </MenuItem>
                    <MenuItem
                      value="GBP"
                      sx={{ color: isDark ? "#FCFCFF" : "rgba(40, 42, 46)" }}
                    >
                      GBP (£)
                    </MenuItem>
                    <MenuItem
                      value="EUR"
                      sx={{ color: isDark ? "#FCFCFF" : "rgba(40, 42, 46)" }}
                    >
                      EUR (€)
                    </MenuItem>
                    <MenuItem
                      value="AUD"
                      sx={{ color: isDark ? "#FCFCFF" : "rgba(40, 42, 46)" }}
                    >
                      AUD (A$)
                    </MenuItem>
                    <MenuItem
                      value="NZD"
                      sx={{ color: isDark ? "#FCFCFF" : "rgba(40, 42, 46)" }}
                    >
                      NZD (NZ$)
                    </MenuItem>
                    <MenuItem
                      value="JPY"
                      sx={{ color: isDark ? "#FCFCFF" : "rgba(40, 42, 46)" }}
                    >
                      JPY (¥)
                    </MenuItem>
                    <MenuItem
                      value="CAD"
                      sx={{ color: isDark ? "#FCFCFF" : "rgba(40, 42, 46)" }}
                    >
                      CAD (C$)
                    </MenuItem>
                    <MenuItem
                      value="CHF"
                      sx={{ color: isDark ? "#FCFCFF" : "rgba(40, 42, 46)" }}
                    >
                      CHF (CHF)
                    </MenuItem>
                  </Select>
                </FormControl>
              </div>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setExpenseDialogOpen(false)}
              style={{
                color: isDark ? "#FCFCFF" : "#1a1b1e",
                cursor: "pointer",
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleExpenseSubmit}
              variant="contained"
              style={{
                backgroundColor: "#2196F3",
                color: "#ffffff",
                cursor: "pointer",
              }}
            >
              {expenseMode === "create" ? "Create" : "Update"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ============ OUTSIDE INCOME DIALOG ============ */}
        <Dialog
          open={outsideIncomeDialogOpen}
          onClose={() => setOutsideIncomeDialogOpen(false)}
          sx={dialogStyle}
        >
          <DialogTitle>
            <Box
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>
                {outsideIncomeMode === "create"
                  ? "Add Outside Income"
                  : "Edit Outside Income"}
              </span>
              <IconButton
                onClick={() => setOutsideIncomeDialogOpen(false)}
                style={{
                  color: isDark ? "#FCFCFF" : "#1a1b1e",
                  cursor: "pointer",
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box
              style={{
                display: "grid",
                gridTemplateColumns: width < 768 ? "1fr" : "1fr 1fr",
                gap: "16px",
                paddingTop: "8px",
              }}
            >
              <div>
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: "500",
                    color: isDark ? "#9ca3af" : "#6b7280",
                    display: "block",
                    marginBottom: "4px",
                  }}
                >
                  Income Source Name
                </span>
                <CustomSingleTextField
                  type="text"
                  size="small"
                  value={outsideIncomeForm.name}
                  onChange={(e) =>
                    setOutsideIncomeForm({
                      ...outsideIncomeForm,
                      name: e.target.value,
                    })
                  }
                  placeholder="e.g., Full-time Job, Freelance, etc."
                />
              </div>

              <div>
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: "500",
                    color: isDark ? "#9ca3af" : "#6b7280",
                    display: "block",
                    marginBottom: "4px",
                  }}
                >
                  Monthly Amount ($)
                </span>
                <CustomSingleTextField
                  type="number"
                  size="small"
                  value={outsideIncomeForm.amount}
                  onChange={(e) =>
                    setOutsideIncomeForm({
                      ...outsideIncomeForm,
                      amount: e.target.value,
                    })
                  }
                  placeholder="Monthly Amount"
                  inputProps={{
                    inputMode: "decimal",
                    pattern: "^[0-9]*[.]?[0-9]*$",
                  }}
                />
              </div>

              <div>
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: "500",
                    color: isDark ? "#9ca3af" : "#6b7280",
                    display: "block",
                    marginBottom: "4px",
                  }}
                >
                  Currency
                </span>
                <FormControl fullWidth>
                  <Select
                    size="small"
                    value={outsideIncomeForm.currency}
                    onChange={(e) =>
                      setOutsideIncomeForm({
                        ...outsideIncomeForm,
                        currency: e.target.value,
                      })
                    }
                    displayEmpty
                    MenuProps={{
                      sx: { zIndex: 99999 },
                      PaperProps: {
                        sx: {
                          bgcolor: isDark ? "rgba(40, 42, 46)" : "#FCFCFF",
                          maxHeight: "450px",
                          "& .MuiMenuItem-root": {
                            padding: 1.5,
                            justifyContent: "center",
                          },
                        },
                      },
                    }}
                    sx={{
                      height: "50px",
                      backgroundColor: isDark ? "#3a3d45" : "#e5e7eb",
                      color: isDark ? "#FCFCFF" : "#1f2937",
                      borderRadius: "12px",
                      fontSize: "14px",
                      fontWeight: 600,
                      ".MuiSvgIcon-root": {
                        fill: isDark ? "#FCFCFF" : "#1f2937",
                      },
                      "& fieldset": {
                        border: "none",
                      },
                      "&:hover": {
                        backgroundColor: isDark ? "#434650" : "#d1d5db",
                      },
                      "&:hover fieldset": {
                        border: "none",
                      },
                      "&.Mui-focused": {
                        backgroundColor: isDark ? "#3a3d45" : "#e5e7eb",
                      },
                      "&.Mui-focused fieldset": {
                        border: "none",
                      },
                      "& .MuiSelect-select": {
                        paddingTop: "12px",
                        paddingBottom: "12px",
                      },
                    }}
                  >
                    <MenuItem
                      value="USD"
                      sx={{ color: isDark ? "#FCFCFF" : "rgba(40, 42, 46)" }}
                    >
                      USD ($)
                    </MenuItem>
                    <MenuItem
                      value="GBP"
                      sx={{ color: isDark ? "#FCFCFF" : "rgba(40, 42, 46)" }}
                    >
                      GBP (£)
                    </MenuItem>
                    <MenuItem
                      value="EUR"
                      sx={{ color: isDark ? "#FCFCFF" : "rgba(40, 42, 46)" }}
                    >
                      EUR (€)
                    </MenuItem>
                    <MenuItem
                      value="AUD"
                      sx={{ color: isDark ? "#FCFCFF" : "rgba(40, 42, 46)" }}
                    >
                      AUD (A$)
                    </MenuItem>
                    <MenuItem
                      value="NZD"
                      sx={{ color: isDark ? "#FCFCFF" : "rgba(40, 42, 46)" }}
                    >
                      NZD (NZ$)
                    </MenuItem>
                    <MenuItem
                      value="JPY"
                      sx={{ color: isDark ? "#FCFCFF" : "rgba(40, 42, 46)" }}
                    >
                      JPY (¥)
                    </MenuItem>
                    <MenuItem
                      value="CAD"
                      sx={{ color: isDark ? "#FCFCFF" : "rgba(40, 42, 46)" }}
                    >
                      CAD (C$)
                    </MenuItem>
                    <MenuItem
                      value="CHF"
                      sx={{ color: isDark ? "#FCFCFF" : "rgba(40, 42, 46)" }}
                    >
                      CHF (CHF)
                    </MenuItem>
                  </Select>
                </FormControl>
              </div>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setOutsideIncomeDialogOpen(false)}
              style={{
                color: isDark ? "#FCFCFF" : "#1a1b1e",
                cursor: "pointer",
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleOutsideIncomeSubmit}
              variant="contained"
              style={{
                backgroundColor: "#2196F3",
                color: "#ffffff",
                cursor: "pointer",
              }}
            >
              {outsideIncomeMode === "create" ? "Create" : "Update"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ============ DELETE CONFIRMATION DIALOG ============ */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          sx={dialogStyle}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Box style={{ paddingTop: "8px" }}>
              <Typography>
                Are you sure you want to delete this {deleteType}?
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              style={{
                color: isDark ? "#FCFCFF" : "#1a1b1e",
                cursor: "pointer",
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              variant="contained"
              style={{
                backgroundColor: "#f23645",
                color: "#ffffff",
                cursor: "pointer",
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default Income;
